import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ShoppingBag, User, Menu, X, ChevronDown, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const languages = [
  { code: 'fr', name: 'FR', flag: '🇫🇷' },
  { code: 'en', name: 'EN', flag: '🇬🇧' },
  { code: 'es', name: 'ES', flag: '🇪🇸' },
];

// Helper to get correct image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/uploads/')) return `${API_URL}${url}`;
  if (url.startsWith('/uploads/')) return `${API_URL}/api${url}`;
  return url;
};

export const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, isAdmin, openAuthModal, logout } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showCategories, setShowCategories] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const lang = i18n.language;
        const [catsRes, settingsRes] = await Promise.all([
          axios.get(`${API_URL}/api/categories?lang=${lang}`),
          axios.get(`${API_URL}/api/settings`)
        ]);
        setCategories(catsRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [i18n.language]);

  const itemCount = cart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];
  const logoUrl = settings?.logo_url ? getImageUrl(settings.logo_url) : '/logo.png';

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    setShowLangMenu(false);
  };

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled 
            ? 'bg-brand-black shadow-lg' 
            : 'bg-brand-black/95 backdrop-blur-sm'
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img 
                src={logoUrl || "/logo.png"} 
                alt={settings?.store_name || 'ALINA ELEGANCE'} 
                className="h-12 sm:h-16 lg:h-20 w-auto object-contain"
                onError={(e) => { e.target.src = '/logo.png'; }}
              />
              <span className="font-heading text-brand-gold text-sm sm:text-xl lg:text-2xl font-bold tracking-wider uppercase">
                ALINA ELEGANCE
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8 xl:gap-10">
              <Link to="/" className="text-brand-white hover:text-brand-gold transition-colors font-body text-sm tracking-wide uppercase elegant-underline">
                {t('nav.home')}
              </Link>
              
              {/* Categories Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setShowCategories(true)}
                onMouseLeave={() => setShowCategories(false)}
              >
                <button className="text-brand-white hover:text-brand-gold transition-colors font-body flex items-center text-sm tracking-wide uppercase">
                  {t('nav.categories')} <ChevronDown className="ml-1 h-4 w-4" />
                </button>
                
                <AnimatePresence>
                  {showCategories && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute top-full left-0 mt-2 w-56 bg-brand-white rounded-sm shadow-xl border border-brand-gold/20 overflow-hidden"
                    >
                      {categories.map(cat => (
                        <Link
                          key={cat.category_id}
                          to={`/products?category=${cat.slug}`}
                          className="block px-5 py-3 text-brand-dark hover:bg-brand-gold hover:text-brand-white transition-colors text-sm tracking-wide"
                          onClick={() => setShowCategories(false)}
                        >
                          {cat.name}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link to="/products" className="text-brand-white hover:text-brand-gold transition-colors font-body text-sm tracking-wide uppercase elegant-underline">
                {t('nav.shop')}
              </Link>
              <Link to="/products?is_new=true" className="text-brand-white hover:text-brand-gold transition-colors font-body text-sm tracking-wide uppercase elegant-underline">
                {t('nav.newArrivals')}
              </Link>
              <Link to="/contact" className="text-brand-white hover:text-brand-gold transition-colors font-body text-sm tracking-wide uppercase elegant-underline">
                {t('nav.contact')}
              </Link>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 sm:gap-4">
              {/* Language Selector - Desktop */}
              <div 
                className="relative hidden sm:block"
                onMouseEnter={() => setShowLangMenu(true)}
                onMouseLeave={() => setShowLangMenu(false)}
              >
                <button 
                  className="flex items-center gap-1.5 px-3 py-2 rounded-sm bg-brand-gold/10 text-brand-gold hover:bg-brand-gold/20 transition-colors text-sm border border-brand-gold/30"
                  data-testid="language-selector"
                >
                  <Globe className="h-4 w-4" />
                  <span className="font-medium">{currentLang.name}</span>
                  <ChevronDown className="h-3 w-3" />
                </button>
                
                <AnimatePresence>
                  {showLangMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute top-full right-0 mt-2 w-32 bg-brand-white rounded-sm shadow-xl border border-brand-gold/20 overflow-hidden"
                    >
                      {languages.map(lang => (
                        <button
                          key={lang.code}
                          onClick={() => changeLanguage(lang.code)}
                          className={`w-full flex items-center gap-2 px-4 py-3 text-left transition-colors text-sm ${
                            i18n.language === lang.code 
                              ? 'bg-brand-gold text-brand-white' 
                              : 'text-brand-dark hover:bg-brand-cream'
                          }`}
                        >
                          <span>{lang.flag}</span>
                          <span className="font-medium">{lang.name}</span>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* Cart */}
              <Link to="/cart" className="relative p-2 text-brand-white hover:text-brand-gold transition-colors" data-testid="cart-btn">
                <ShoppingBag className="h-5 w-5 sm:h-6 sm:w-6" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-brand-gold text-brand-black text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                    {itemCount}
                  </span>
                )}
              </Link>

              {/* User Menu - Desktop */}
              {isAuthenticated ? (
                <div className="hidden md:flex items-center gap-1">
                  {isAdmin && (
                    <Link to="/admin">
                      <Button variant="ghost" size="sm" className="text-brand-gold hover:bg-brand-gold/10 text-xs px-3 border border-brand-gold/30">
                        Admin
                      </Button>
                    </Link>
                  )}
                  <Link to="/orders">
                    <Button variant="ghost" size="sm" className="text-brand-white hover:text-brand-gold hover:bg-brand-gold/10 text-xs px-3">
                      {t('nav.orders')}
                    </Button>
                  </Link>
                  <Button variant="ghost" size="sm" onClick={logout} className="text-brand-white hover:text-brand-gold hover:bg-brand-gold/10 text-xs px-3">
                    {t('nav.logout')}
                  </Button>
                </div>
              ) : (
                <Button 
                  onClick={() => openAuthModal('login')}
                  className="hidden md:flex bg-brand-gold text-brand-black hover:bg-brand-gold/90 transition-all duration-300 text-sm px-4 py-2 rounded-sm font-medium"
                  data-testid="login-btn"
                >
                  <User className="h-4 w-4 mr-2" /> {t('nav.login')}
                </Button>
              )}

              {/* Mobile Menu Toggle */}
              <button 
                className="lg:hidden p-2 text-brand-white hover:text-brand-gold"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-5 w-5 sm:h-6 sm:w-6" /> : <Menu className="h-5 w-5 sm:h-6 sm:w-6" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 lg:hidden pt-16 sm:pt-20"
          >
            <div className="absolute inset-0 bg-brand-black/90" onClick={() => setMobileMenuOpen(false)} />
            <motion.div 
              className="relative bg-brand-black shadow-xl max-h-[85vh] overflow-y-auto border-t border-brand-gold/20"
              initial={{ y: -20 }}
              animate={{ y: 0 }}
            >
              <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                {/* Mobile Language Selector */}
                <div className="flex gap-2 pb-4 border-b border-brand-gold/20">
                  {languages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`flex-1 py-2 rounded-sm font-medium transition-colors text-sm ${
                        i18n.language === lang.code 
                          ? 'bg-brand-gold text-brand-black' 
                          : 'bg-brand-gold/10 text-brand-gold border border-brand-gold/30 hover:bg-brand-gold/20'
                      }`}
                    >
                      {lang.flag} {lang.name}
                    </button>
                  ))}
                </div>

                <Link to="/" className="block py-3 text-brand-white font-body tracking-wide border-b border-brand-gold/10 hover:text-brand-gold text-sm uppercase" onClick={() => setMobileMenuOpen(false)}>
                  {t('nav.home')}
                </Link>
                <Link to="/products" className="block py-3 text-brand-white font-body tracking-wide border-b border-brand-gold/10 hover:text-brand-gold text-sm uppercase" onClick={() => setMobileMenuOpen(false)}>
                  {t('nav.shop')}
                </Link>
                <Link to="/products?is_new=true" className="block py-3 text-brand-white font-body tracking-wide border-b border-brand-gold/10 hover:text-brand-gold text-sm uppercase" onClick={() => setMobileMenuOpen(false)}>
                  {t('nav.newArrivals')}
                </Link>
                <Link to="/contact" className="block py-3 text-brand-white font-body tracking-wide border-b border-brand-gold/10 hover:text-brand-gold text-sm uppercase" onClick={() => setMobileMenuOpen(false)}>
                  {t('nav.contact')}
                </Link>
                
                <div className="pt-4">
                  <p className="text-brand-gold text-xs uppercase tracking-widest mb-3">{t('nav.categories')}</p>
                  {categories.map(cat => (
                    <button
                      key={cat.category_id}
                      className="block w-full text-left py-2 text-brand-white/70 hover:text-brand-gold text-sm"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        navigate(`/products?category=${cat.slug}`);
                        window.scrollTo(0, 0);
                      }}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>

                <div className="pt-6 space-y-3">
                  {isAuthenticated ? (
                    <>
                      {isAdmin && (
                        <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                          <Button className="w-full bg-brand-gold text-brand-black hover:bg-brand-gold/90 text-sm rounded-sm">Admin Panel</Button>
                        </Link>
                      )}
                      <Link to="/orders" onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="outline" className="w-full border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-brand-black text-sm rounded-sm">{t('nav.orders')}</Button>
                      </Link>
                      <Button variant="ghost" className="w-full text-brand-white hover:text-brand-gold hover:bg-brand-gold/10 text-sm" onClick={() => { logout(); setMobileMenuOpen(false); }}>
                        {t('nav.logout')}
                      </Button>
                    </>
                  ) : (
                    <Button 
                      onClick={() => { openAuthModal('login'); setMobileMenuOpen(false); }}
                      className="w-full bg-brand-gold text-brand-black hover:bg-brand-gold/90 text-sm rounded-sm"
                    >
                      <User className="h-4 w-4 mr-2" /> {t('nav.login')}
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
